import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { create } from "zustand";
import { TextField, Tooltip } from "@mui/material";
import { CircularProgress } from "@mui/material";
import {
  addQueryAnswer,
  failOutboundQuery,
  updateWorkspaceQueries,
} from "../../../redux/fpAppV4Slice";
import fpFetch from "../../../services/fpFetch";
import { saveWorkspaceContent } from "../../FpAppV4/services/saveWorkspaceContent";
import { BASE_API_URL } from "../../../constants";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FpProgressBar from "../../common/FpProgressBar";
import toolExecutionAvgTime from "./../../FpAppV4/services/toolExecutionAvgTime";
import { markQueryAsStopped } from "./../../FpAppV4/services/updateQueryStatus";
import { handleError } from "../../../utils/handleError";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";
import {
  deleteEditorNode,
  updateEditorNode,
} from "./services/editorNodeOperations";
import {
  hasStoredAnswer,
  submitWithRerunOnConflict,
} from "./services/queryRerun";
import { v4 as uuidv4 } from "uuid";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  GlobeSimpleIcon,
  CalculatorIcon,
  SlidersIcon,
  ClockIcon,
  CheckIcon,
  XIcon,
  CaretDown,
  Folder,
} from "@phosphor-icons/react";

export type ToolOption = "web" | "calculator" | null;

import { RootState } from "@/redux/store";
import { FpAppV4DataType } from "@/components/FpAppV4/utils/fpAppV4Interface";
import {
  useFileSelection,
  parseSelectedNodes,
  buildSelectedNodes,
  folderSelectionsToFolderPaths,
  expandSelectionsToFileIds,
} from "@/hooks/useFileTreeV2";
import { useWorkspaceContext } from "../services/WorkspaceContext";
import useIsWrongOrg from "../../FpAppV4/hooks/useIsWrongOrg";
import { useWorkspaceStore } from "@/components/FpAppV4/hooks/useDefaultSelection";

// The high-quality toggle is remembered across mounts for the life of the
// page: the next query input starts where the last one was left. A store
// rather than a module variable, so it has an owner the story harness can
// reset between stories (rules/frontend.md, "Module state and effects",
// #12187).
const useHighQualityMemory = create<{
  on: boolean;
  setOn: (on: boolean) => void;
}>((set) => ({ on: false, setOn: (on) => set({ on }) }));

const QueryInput = ({
  qid,
  mountReason,
  selectedTool,
  onToolChange,
  isExtendedThinking: initialIsExtendedThinking,
  onExtendedThinkingChange,
}: {
  qid: string;
  mountReason: string;
  selectedTool?: ToolOption;
  onToolChange?: (tool: ToolOption) => void;
  isExtendedThinking?: boolean;
  onExtendedThinkingChange?: (enabled: boolean) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fpAppV4Data = useSelector(
    (state: RootState) => state.fpAppV4Data as unknown as FpAppV4DataType
  );

  const [query, setQuery] = useState("");
  const userData = useSelector((state) => (state as RootState).userData);
  const [queryInputStatus, setQueryInputStatus] = useState("writing_query");
  const [toolId, setToolId] = useState<string | null>(null);
  const [queryUpdatedByUser, setQueryUpdatedByUser] = useState(false);
  const [querySuggestions, setQuerySuggestions] = useState([
    "Summarize the document",
    "Extract the table from page",
    "Compare the document with",
    "Get all the table data from page",
  ]);
  const [showQuerySuggestions, setShowQuerySuggestions] = useState(false);
  const [selectedQuerySuggestionIndex, setSelectedQuerySuggestionIndex] =
    useState(-1);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showThinkingDropdown, setShowThinkingDropdown] = useState(false);
  const [lineCount, setLineCount] = useState(() => {
    if (mountReason === "initialLoad") {
      return 1;
    }
    return 1;
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const hasCheckedInitialCollapse = useRef(false);
  const lineCountRef = useRef(lineCount);
  lineCountRef.current = lineCount;
  const qidRef = useRef(qid);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);
  const thinkingDropdownRef = useRef<HTMLDivElement>(null);
  const isProgrammaticFocusRef = useRef(false);

  const { workspaceId: contextWorkspaceId } = useWorkspaceContext();
  const workspaceId = contextWorkspaceId || fpAppV4Data.workspaceId;
  const isWrongOrg = useIsWrongOrg(workspaceId);

  const type = fpAppV4Data.type || "private_doc_editor";

  const isReport = type === "private_doc_editor";

  // Read shared store setter for propagating selection between report prompts
  const setDefaultSelection = useWorkspaceStore((s) => s.setDefaultSelection);

  // Compute initial file selection once at mount time. For reports, prefer the
  // shared store (updated when any prompt's selection changes) so new prompts
  // inherit the most recent file selection from sibling prompts. Falls back to
  // the backend workspace default (meta.defaultFileTreeScope).
  const initialSelectionRef = useRef<{
    initialFileIds?: string[];
    initialFolderPaths?: string[][];
  } | null>(null);
  if (initialSelectionRef.current === null) {
    const storeSelection = isReport
      ? useWorkspaceStore.getState().defaultSelections?.[workspaceId || ""]
      : undefined;
    const nodes =
      (storeSelection as any)?.selectedNodes ||
      fpAppV4Data.defaultQueryInputTree?.selectedNodes;
    if (nodes) {
      const { fileIds, folderSelections } = parseSelectedNodes(nodes);
      const folderPaths = folderSelectionsToFolderPaths(folderSelections);
      initialSelectionRef.current = {
        initialFileIds: fileIds.length > 0 ? fileIds : undefined,
        initialFolderPaths: folderPaths.length > 0 ? folderPaths : undefined,
      };
    } else {
      initialSelectionRef.current = {};
    }
  }
  const { initialFileIds, initialFolderPaths } = initialSelectionRef.current;

  // V2: useFileSelection hook for file tree selection
  const {
    searchBar,
    treeContent,
    selectedFileIds,
    selectedFolderSelections,
    selectionControls,
  } = useFileSelection({
    initialFileIds,
    initialFolderPaths,
  });

  // Propagate selection changes to shared store so new report prompts inherit
  // them. Only active for reports. Skips writing empty selection while waiting
  // for initial selection to apply.
  const hasWrittenBack = useRef(false);
  const expectsInitial = useRef(
    (initialFileIds?.length ?? 0) > 0 || (initialFolderPaths?.length ?? 0) > 0
  );
  useEffect(() => {
    if (!isReport || !workspaceId) return;
    const hasSelection =
      selectedFileIds.length > 0 || selectedFolderSelections.length > 0;
    if (!hasWrittenBack.current && expectsInitial.current && !hasSelection) {
      return;
    }
    hasWrittenBack.current = true;
    const nodes = buildSelectedNodes(selectedFileIds, selectedFolderSelections);
    setDefaultSelection(workspaceId, { selectedNodes: nodes } as any);
  }, [
    selectedFileIds,
    selectedFolderSelections,
    workspaceId,
    setDefaultSelection,
    isReport,
  ]);

  const [showFileTree, setShowFileTree] = useState(false);
  const [isExtendedThinking, setIsExtendedThinking] = useState(() =>
    initialIsExtendedThinking !== undefined
      ? initialIsExtendedThinking
      : localStorage.getItem("fp_thinking_effort") === "extended"
  );

  const [isHighQuality, setIsHighQuality] = useState<boolean>(
    () => useHighQualityMemory.getState().on
  );

  const toggleHighQuality = () => {
    const next = !isHighQuality;
    setIsHighQuality(next);
    useHighQualityMemory.getState().setOn(next);
    inputRef.current?.focus();
  };

  const toggleTool = (tool: ToolOption) => {
    const newTool = selectedTool === tool ? null : tool;
    if (onToolChange) {
      onToolChange(newTool);
    }
    setShowToolsDropdown(false);
    // Focus the input after tool selection
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const toggleExtendedThinking = () => {
    const next = !isExtendedThinking;
    setIsExtendedThinking(next);
    localStorage.setItem("fp_thinking_effort", next ? "extended" : "standard");
    if (onExtendedThinkingChange) {
      onExtendedThinkingChange(next);
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    qidRef.current = qid;
  }, [qid]);
  const dispatch = useDispatch();

  // Track line count
  useEffect(() => {
    const lines = (query.match(/\n/g) || []).length + 1;
    setLineCount(lines);
    lineCountRef.current = lines;
  }, [query]);

  // Visual overflow detection for long lines that wrap
  useEffect(() => {
    const textarea =
      inputRef.current?.querySelector?.("textarea") || inputRef.current;
    if (!textarea) return;

    const checkOverflow = () => {
      const el = textarea as HTMLTextAreaElement;
      const style = window.getComputedStyle(el);
      const lineHeight = parseFloat(style.lineHeight) || 20;
      const visualLines = Math.round(el.scrollHeight / lineHeight);
      const overflows = visualLines > 3;
      setIsOverflowing(overflows);

      // Auto-collapse on initial load if overflowing
      if (
        !hasCheckedInitialCollapse.current &&
        !queryUpdatedByUser &&
        overflows
      ) {
        hasCheckedInitialCollapse.current = true;
        setIsCollapsed(true);
      }

      // Auto-expand if content fits within 3 visual lines
      if (!overflows && lineCountRef.current <= 3) {
        setIsCollapsed(false);
      }
    };

    // Check after render
    const rafId = requestAnimationFrame(checkOverflow);

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(textarea);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [query, lineCount, queryUpdatedByUser]);

  useEffect(() => {
    if (mountReason === "initialLoad") {
      inputRef.current?.focus();
    }
  }, []);

  // Handle click outside to close tools dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showToolsDropdown &&
        toolsDropdownRef.current &&
        !toolsDropdownRef.current.contains(event.target as Node)
      ) {
        setShowToolsDropdown(false);
      }
      if (
        showThinkingDropdown &&
        thinkingDropdownRef.current &&
        !thinkingDropdownRef.current.contains(event.target as Node)
      ) {
        setShowThinkingDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showToolsDropdown, showThinkingDropdown]);

  // The query text reaches the backend and the store on the product timer
  // module (#12187). A lodash debounce kept firing after the cell unmounted;
  // the shared hook cancels on unmount instead, so this effect, declared
  // before the hooks and so cleaned up before them, sends what is still
  // pending first. The last keystrokes before a cell is removed still land.
  const flushQueryUpdatesRef = useRef<() => void>(() => undefined);
  useEffect(() => () => flushQueryUpdatesRef.current(), []);

  const debouncedBackendUpdate = useDebouncedCallback((query: string) => {
    fpFetch(`${BASE_API_URL}/workspaces/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        _id: workspaceId,
        type: type || "private_doc_editor",
        queries: {
          [qidRef.current]: { query },
        },
      }),
    }).finally(() => {
      window.savingWorkspaceContent = false;
      window.dispatchEvent(new Event("savingContentStateChange"));
    });
  }, 500);

  const debouncedLocalUpdate = useDebouncedCallback((query: string) => {
    dispatch(
      updateWorkspaceQueries({
        [qidRef.current]: {
          query,
          isFocused: true,
        },
      })
    );
  }, 100);

  flushQueryUpdatesRef.current = () => {
    debouncedLocalUpdate.flush();
    debouncedBackendUpdate.flush();
  };

  useEffect(() => {
    if (queryUpdatedByUser) {
      debouncedLocalUpdate(query);
      debouncedBackendUpdate(query);
    }
  }, [query]);

  // Re-point this cell at a brand-new qid, preserving the given prompt text.
  // A query_answers id is immutable and equals the qid, so re-running an
  // already-answered cell must run under a fresh id. Mirrors the STOP/RETRY
  // path (cancelQueryFromExecution): rewire the editor node, persist the
  // prompt for the new qid, and force-save the rewired content so the new qid
  // is pinned in workspaces.content immediately. Returns the new qid.
  const rewireToFreshQid = async (queryText: string): Promise<string> => {
    const newQid = uuidv4();
    const oldQid = qidRef.current;

    dispatch(
      updateWorkspaceQueries({
        [newQid]: {
          query: queryText,
          isFocused: true,
        },
      })
    );
    fpFetch(`${BASE_API_URL}/workspaces/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        _id: workspaceId,
        type: type || "private_doc_editor",
        queries: {
          [newQid]: { query: queryText },
        },
      }),
    }).finally(() => {
      window.savingWorkspaceContent = false;
      window.dispatchEvent(new Event("savingContentStateChange"));
    });

    await updateEditorNode(
      (window as Window).fp.editor,
      "queryInput",
      { qid: oldQid },
      { qid: newQid }
    );
    const editor = (window as Window).fp.editor;
    if (editor) {
      saveWorkspaceContent({ content: editor.getHTML(), workspaceId });
    }
    qidRef.current = newQid;
    qid = newQid;
    return newQid;
  };

  interface HandleSubmitArgs {
    status?: string;
    batchSize?: number;
  }

  const handleSubmit = async ({
    status = queryInputStatus,
    batchSize = 1,
  }: HandleSubmitArgs = {}) => {
    if (type?.includes("template")) {
      (window as Window).fp.editor?.commands.focus();
      return;
    }
    if (!query || status !== "writing_query") return;

    // A query_answers id is immutable and equals the qid. If this cell already
    // has a stored answer (known client-side), a re-run must use a fresh qid;
    // otherwise the backend rejects it and the run is silently dropped (#10539).
    if (hasStoredAnswer(qidRef.current, fpAppV4Data.queryAnswers)) {
      await rewireToFreshQid(query);
    }

    dispatch(
      addQueryAnswer({
        qid: qidRef.current,
        query: query,
        added: false,
        status: "IN_QUEUE",
      })
    );

    setQueryInputStatus("IN_QUEUE");
    setQueryUpdatedByUser(false); // Reset when submitting a new query
    if (lineCount > 3 || isOverflowing) {
      setIsCollapsed(true);
    }

    // For querygrid workspaces, treat queries as matrix chat queries
    // This ensures they use matrix cells as sources and get inserted into workspace content
    const isMatrixChat = type === "querygrid";

    // Resolve folder selections to file IDs before sending the request.
    // When folders are selected from search results, they exist only as
    // folder paths (not expanded file IDs), so we must expand them.
    let fileIds = selectedFileIds;
    if (selectedFolderSelections.length > 0) {
      try {
        const result = await expandSelectionsToFileIds(
          selectedFileIds,
          selectedFolderSelections
        );
        fileIds = result.files.map((f) => f.fileId);
      } catch (e) {
        dispatch(failOutboundQuery({ qid: qidRef.current, error: String(e) }));
        return;
      }
    }

    const runQuery = () =>
      fpFetch(`${BASE_API_URL}/queryResults/fp0040a`, {
        ["x-finpilot-auth"]: "require",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query,
          workspaceId: workspaceId,
          wsDiscriminator: "default",
          _id: qidRef.current,
          userDocs: {
            docIds: fileIds,
            userId: userData.userId,
          },
          tabIndex:
            fpAppV4Data?.queryAnswers && fpAppV4Data.queryAnswers.length
              ? fpAppV4Data.queryAnswers.length + 1
              : 1,
          // The viewer's IANA zone, read fresh on every send rather than
          // stored, so it follows the person across a flight. This node is the
          // interactive path -- a person typing into a report or query-grid
          // cell and running it now. A *scheduled* report run never reaches
          // this component; it builds its GetAnswerArgs server-side with no
          // timezone and correctly gets the organization's calendar (#11055).
          timezone: currentViewerTimeZone(),
          ...(isHighQuality && { quality: "high" }),
          ...(selectedTool === "web" ? { isWebSearch: true } : {}),
          ...(selectedTool === "calculator" ? { isCalculator: true } : {}),
          ...(isMatrixChat && { isMatrixChat: true }),
          ...(isExtendedThinking
            ? { extended_thinking: true }
            : { extended_thinking: false }),
          batchSize: batchSize,
        }),
      });

    // The backend rejects a run whose qid already has an answer with a
    // 409/QID_EXISTS signal (the desync case redux can't see up front). Re-run
    // once under a fresh qid, preserving the edited prompt, instead of leaving
    // the cell stuck spinning on a dropped request. Any other non-ok result is
    // surfaced as a failure rather than swallowed (fpFetch does not throw on it).
    try {
      await submitWithRerunOnConflict({
        run: async () => {
          const response = await runQuery();
          return {
            ok: Boolean(response?.ok),
            status: response?.status ?? 0,
            readBody: async () => {
              try {
                return await response.clone().json();
              } catch {
                return null;
              }
            },
          };
        },
        rewire: async () => {
          await rewireToFreshQid(query);
          dispatch(
            addQueryAnswer({
              qid: qidRef.current,
              query: query,
              added: false,
              status: "IN_QUEUE",
            })
          );
        },
        onFail: (status) =>
          dispatch(
            failOutboundQuery({
              qid: qidRef.current,
              error: `Query request failed with status ${status ?? "unknown"}`,
            })
          ),
      });
    } catch (e) {
      dispatch(failOutboundQuery({ qid: qidRef.current, error: String(e) }));
    }
  };

  useEffect(() => {
    if (fpAppV4Data.queries) {
      const query = fpAppV4Data.queries[qidRef.current];
      if (
        query &&
        query.startExecution &&
        queryInputStatus === "writing_query"
      ) {
        handleSubmit({
          batchSize:
            // Shouldn't be necessary, but just in case there's any holdover from old code, convert.
            typeof query.startExecution === "boolean"
              ? 5
              : Number(query.startExecution),
        });
      }
    }
  }, [fpAppV4Data.queries]);

  useEffect(() => {
    const queryAnswer = fpAppV4Data.queryAnswers?.find(
      (qa) => qa.qid === qidRef.current
    );
    const queryData = fpAppV4Data.queries?.[qidRef.current];

    if (queryAnswer) {
      if (queryAnswer.status === "canceled") {
        deleteEditorNode((window as Window).fp.editor, "queryInput", {
          qid: qidRef.current,
        });
        return;
      }

      // Only update query if user hasn't modified it OR query status has changed
      if (!queryUpdatedByUser || queryAnswer.status !== "writing_query") {
        const loadedQuery = queryAnswer.query || queryData?.query || "";
        setQuery(loadedQuery);
        setQueryInputStatus(queryAnswer.status || "writing_query");
        setToolId(queryAnswer.toolId || null);

        // Track line count from loaded query
        const lines = (loadedQuery.match(/\n/g) || []).length + 1;
        setLineCount(lines);
        lineCountRef.current = lines;

        // Auto-collapse on initial load for long queries
        if (
          !hasCheckedInitialCollapse.current &&
          !queryUpdatedByUser &&
          lines > 3
        ) {
          hasCheckedInitialCollapse.current = true;
          setIsCollapsed(true);
        }

        // Reset queryUpdatedByUser when query completes or fails
        if (queryAnswer.status !== "writing_query") {
          setQueryUpdatedByUser(false);
        }
      }
    } else if (queryData && !queryUpdatedByUser) {
      // Only update from queryData if user hasn't modified it
      const loadedQuery = queryData.query || "";
      setQuery(loadedQuery);
      setQueryInputStatus("writing_query");
      setToolId(null);

      // Track line count from loaded query
      const lines = (loadedQuery.match(/\n/g) || []).length + 1;
      setLineCount(lines);
      lineCountRef.current = lines;

      // Auto-collapse on initial load for long queries
      if (
        !hasCheckedInitialCollapse.current &&
        !queryUpdatedByUser &&
        lines > 3
      ) {
        hasCheckedInitialCollapse.current = true;
        setIsCollapsed(true);
      }
    }
  }, [
    fpAppV4Data.queryAnswers,
    fpAppV4Data.queries,
    qid,
    qidRef.current,
    queryUpdatedByUser,
  ]);

  const cancelQueryFromExecution = async () => {
    try {
      const currentQuerySnapshot = query;
      let fireAgain = false;
      markQueryAsStopped(qidRef.current);
      if (queryInputStatus !== "failed") {
        fpFetch(`${BASE_API_URL}/queryResults/fp0049a`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            _id: qidRef.current,
          }),
        });
      } else {
        fireAgain = true;
      }
      const newQid = uuidv4();

      // Preserve the current prompt for the new qid locally and in backend before switching
      dispatch(
        updateWorkspaceQueries({
          [newQid]: {
            query: currentQuerySnapshot,
            isFocused: true,
          },
        })
      );
      fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: workspaceId,
          type: type || "private_doc_editor",
          queries: {
            [newQid]: { query: currentQuerySnapshot },
          },
        }),
      }).finally(() => {
        window.savingWorkspaceContent = false;
        window.dispatchEvent(new Event("savingContentStateChange"));
      });

      await updateEditorNode(
        (window as Window).fp.editor,
        "queryInput",
        { qid: qidRef.current },
        {
          qid: newQid,
        }
      );
      // Force-save the rewired content immediately. The editor's onUpdate
      // handler skips its first invocation, so a retry clicked before any
      // other edit would otherwise leave the old failed qid pinned in
      // workspaces.content and the cell stuck on the failed query forever.
      const editor = (window as Window).fp.editor;
      if (editor) {
        saveWorkspaceContent({ content: editor.getHTML(), workspaceId });
      }
      setQueryInputStatus("writing_query");
      setQuery(currentQuerySnapshot);
      setQueryUpdatedByUser(false); // Reset when cancelling/retrying
      qidRef.current = newQid;
      // NEW: Selection persists automatically via workspace integration
      qid = newQid;

      if (fireAgain) {
        handleSubmit({ status: "writing_query" });
      }
    } catch (error) {
      handleError({
        error,
        location: "cancelQueryFromExecution",
      });
    }
  };

  return (
    <div className="w-full group">
      {queryInputStatus !== "writing_query" && (
        <div className="w-full flex justify-end items-center text-[9px]">
          {queryInputStatus === "failed" && (
            <div className="mr-4">
              <span className="font-bold">Failed</span> : An error occured while
              executing the query
            </div>
          )}
          <Tooltip
            title={
              isWrongOrg && queryInputStatus === "failed"
                ? "Disabled - check your organization."
                : ""
            }
            arrow
          >
            <div
              className={`flex items-center p-1 rounded-md rounded-b-none transition-all text-gray-500 ${
                isWrongOrg && queryInputStatus === "failed"
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer group-hover:text-white group-hover:bg-gray-500 hover:bg-gray-500 hover:text-white"
              }`}
              onClick={
                isWrongOrg && queryInputStatus === "failed"
                  ? undefined
                  : cancelQueryFromExecution
              }
            >
              {queryInputStatus === "failed" ? (
                <ReplayIcon
                  style={{
                    fontSize: "11px",
                  }}
                />
              ) : (
                <StopCircleIcon
                  style={{
                    fontSize: "11px",
                  }}
                />
              )}
              <span className="ml-1">
                {queryInputStatus === "failed" ? "RETRY" : "STOP"}
              </span>
            </div>
          </Tooltip>
        </div>
      )}
      <div
        className={`rounded-md bg-gray-100 shadow-sm w-full p-2 ${queryInputStatus !== "writing_query" ? (queryInputStatus !== "failed" ? "animate-pulse" : "opacity-50") : ""}`}
      >
        <div className="flex w-full">
          <div className="w-full px-2 rounded-md flex items-center bg-white">
            <div
              className="w-full relative"
              style={{
                overflow: isCollapsed ? "hidden" : undefined,
              }}
            >
              <TextField
                inputRef={inputRef}
                type="text"
                className="w-full"
                placeholder={
                  isWrongOrg
                    ? "Disabled - check your organization."
                    : type?.includes("private_doc_editor")
                      ? "Ask Finpilot..."
                      : "Ask your query..."
                }
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setQueryUpdatedByUser(true);
                }}
                disabled={isWrongOrg || queryInputStatus !== "writing_query"}
                maxRows={isCollapsed ? 3 : undefined}
                onCopy={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (query) handleSubmit();
                    else {
                      if (selectedQuerySuggestionIndex > -1) {
                        setQuery(
                          querySuggestions[selectedQuerySuggestionIndex] + " "
                        );
                        setQueryUpdatedByUser(true);
                      }
                    }
                  }
                  if (e.key === "Escape") {
                    deleteEditorNode(
                      (window as Window).fp.editor,
                      "queryInput",
                      {
                        qid: qidRef.current,
                      }
                    );
                  }
                  // if user press backspace and query is empty
                  if (e.key === "Backspace" && !query) {
                    deleteEditorNode(
                      (window as Window).fp.editor,
                      "queryInput",
                      {
                        qid: qidRef.current,
                      },
                      {
                        bringBackCursor: true,
                      }
                    );
                  }

                  if (e.key === "/") {
                    e.stopPropagation();
                  }

                  if (
                    (e.ctrlKey || e.metaKey) &&
                    (e.key.toLowerCase() === "c" || e.key.toLowerCase() === "v")
                  ) {
                    e.stopPropagation();
                  }

                  if (e.key === "ArrowUp") {
                    setSelectedQuerySuggestionIndex((prev) => {
                      if (prev > 0) {
                        return prev - 1;
                      } else {
                        return 0;
                      }
                    });
                  }

                  if (e.key === "ArrowDown") {
                    setSelectedQuerySuggestionIndex((prev) => {
                      if (prev < querySuggestions.length - 1) {
                        return prev + 1;
                      } else {
                        return querySuggestions.length - 1;
                      }
                    });
                  }
                }}
                multiline
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    padding: 0,
                    outline: "none",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 14,
                  },
                  "& .MuiInputBase-inputMultiline": {
                    padding: "5px 10px",
                    ...(isCollapsed && {
                      overflow: "hidden",
                    }),
                  },
                }}
                onFocus={() => {
                  dispatch(
                    updateWorkspaceQueries({
                      [qidRef.current]: {
                        query: query,
                        isFocused: true,
                      },
                    })
                  );
                  if (!isProgrammaticFocusRef.current) {
                    setShowQuerySuggestions(true);
                  }
                  isProgrammaticFocusRef.current = false;
                }}
                onBlur={() => {
                  // A suggestion's onMouseDown prevents default, so clicking
                  // one never blurs the input in the first place — this only
                  // ever fires for a real outside blur.
                  setShowQuerySuggestions(false);
                }}
              />
              {/* Collapse/expand footer */}
              {(lineCount > 3 || isOverflowing) && (
                <div
                  style={{
                    position: isCollapsed ? "absolute" : "relative",
                    bottom: isCollapsed ? 0 : undefined,
                    left: 0,
                    right: 0,
                  }}
                >
                  {/* Gradient fade (only when collapsed) */}
                  {isCollapsed && (
                    <div
                      style={{
                        height: 20,
                        background:
                          "linear-gradient(to bottom, transparent, white)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  {/* Solid background strip with toggle button */}
                  <div
                    style={{
                      background: "white",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsCollapsed((prev) => !prev)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        fontSize: 11,
                        color: "#6b7280",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}
                      className="hover:bg-gray-100"
                    >
                      {isCollapsed ? (
                        <>
                          <KeyboardArrowDownIcon style={{ fontSize: 14 }} />
                          <span>Show more</span>
                        </>
                      ) : (
                        <>
                          <KeyboardArrowUpIcon style={{ fontSize: 14 }} />
                          <span>Show less</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              className={`flex items-center ${type === "editor_template" || type === "private_doc_editor_template" ? "hidden" : ""}`}
            >
              {queryInputStatus !== "writing_query" &&
              queryInputStatus !== "failed" ? (
                <CircularProgress
                  size={16}
                  style={{
                    marginRight: "8px",
                    color: "gray",
                  }}
                />
              ) : queryInputStatus !== "failed" ? (
                <Tooltip
                  title={
                    isWrongOrg ? "Disabled - check your organization." : ""
                  }
                >
                  <ArrowUpwardIcon
                    className={
                      isWrongOrg
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }
                    style={{ fontSize: 16, color: "gray" }}
                    onClick={isWrongOrg ? undefined : () => handleSubmit()}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
        {showQuerySuggestions &&
          !isWrongOrg &&
          fpAppV4Data.type?.includes("private_doc_editor") &&
          !query && (
            <div className="w-full  bg-red-100 relative ">
              <div className="absolute top-[-20px] left-[27px] w-[calc(100%-60px)] text-[13px] bg-white rounded-b-md text-gray-500 shadow-lg pb-2 z-10">
                {querySuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`p-1 px-2 pl-4 hover:bg-gray-100 cursor-pointer query-suggestion ${selectedQuerySuggestionIndex === index ? "bg-gray-400 text-white" : ""}`}
                    tabIndex={0}
                    onMouseDown={(e) => {
                      // Keep focus on the input so the click never blurs it.
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      setQuery(suggestion + " ");
                      setQueryUpdatedByUser(true);
                      inputRef.current?.focus();
                      e.stopPropagation();
                    }}
                  >
                    {suggestion + " ..."}
                  </div>
                ))}
              </div>
            </div>
          )}
        {(type === "private_doc_editor" ||
          type === "private_doc_editor_template") &&
          queryInputStatus === "writing_query" && (
            <div className="w-full mt-4">
              {/* Header with collapse toggle, tools, thinking, and selection controls */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {isReport && (
                    <button
                      onClick={() => {
                        setShowFileTree(!showFileTree);
                        isProgrammaticFocusRef.current = true;
                        inputRef.current?.focus();
                      }}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <Folder className="w-4 h-4" />
                      <span>{showFileTree ? "Hide" : "Show"} Files</span>
                      <CaretDown
                        className={`w-3 h-3 transition-transform ${showFileTree ? "rotate-180" : ""}`}
                      />
                    </button>
                  )}
                  {queryInputStatus === "writing_query" && (
                    <>
                      {/* Tools dropdown */}
                      <div className="relative" ref={toolsDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedTool) {
                              toggleTool(selectedTool);
                            } else {
                              setShowToolsDropdown((p) => !p);
                            }
                          }}
                          disabled={isWrongOrg}
                          className={`group flex items-center gap-1.5 text-sm transition-colors ${
                            selectedTool
                              ? "text-gray-600 hover:text-gray-800"
                              : "text-gray-500 hover:text-gray-700"
                          } ${isWrongOrg ? "cursor-default opacity-75" : "cursor-pointer"}`}
                        >
                          {selectedTool ? (
                            <>
                              <span className="group-hover:hidden">
                                {selectedTool === "web" ? (
                                  <GlobeSimpleIcon className="w-4 h-4" />
                                ) : (
                                  <CalculatorIcon className="w-4 h-4" />
                                )}
                              </span>
                              <XIcon className="w-4 h-4 hidden group-hover:block text-gray-400" />
                            </>
                          ) : (
                            <SlidersIcon className="w-4 h-4" />
                          )}
                          <span>
                            {selectedTool === "web"
                              ? "Search"
                              : selectedTool === "calculator"
                                ? "Calculate"
                                : "Tools"}
                          </span>
                        </button>

                        {showToolsDropdown && (
                          <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-40 overflow-hidden py-1">
                            <div className="px-3 py-1.5 text-xs text-gray-400 font-medium">
                              Tools
                            </div>
                            <Tooltip
                              title="Search the internet for information"
                              placement="right"
                              arrow
                            >
                              <button
                                type="button"
                                onClick={() => toggleTool("web")}
                                className={`flex w-full items-center gap-3 px-3 py-2 text-sm ${
                                  selectedTool === "web"
                                    ? "text-gray-900"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span className="flex-1 text-left">
                                  Web search
                                </span>
                                {selectedTool === "web" && (
                                  <CheckIcon className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </Tooltip>
                            <Tooltip
                              title="Run calculations on your data"
                              placement="right"
                              arrow
                            >
                              <button
                                type="button"
                                onClick={() => toggleTool("calculator")}
                                className={`flex w-full items-center gap-3 px-3 py-2 text-sm ${
                                  selectedTool === "calculator"
                                    ? "text-gray-900"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span className="flex-1 text-left">
                                  Calculation
                                </span>
                                {selectedTool === "calculator" && (
                                  <CheckIcon className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {/* Thinking effort dropdown */}
                      <div className="relative" ref={thinkingDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setShowThinkingDropdown((p) => !p)}
                          disabled={isWrongOrg}
                          className={`flex items-center gap-1.5 text-sm transition-colors ${
                            isExtendedThinking
                              ? "text-gray-600 hover:text-gray-800"
                              : "text-gray-500 hover:text-gray-700"
                          } ${isWrongOrg ? "cursor-default opacity-75" : "cursor-pointer"}`}
                        >
                          <ClockIcon className="w-4 h-4" />
                          <span>
                            {isExtendedThinking
                              ? "Extended thinking"
                              : "Thinking"}
                          </span>
                        </button>

                        {showThinkingDropdown && (
                          <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-40 overflow-hidden py-1">
                            <div className="px-3 py-1.5 text-xs text-gray-400 font-medium">
                              Thinking effort
                            </div>
                            <Tooltip
                              title="Balances depth and speed"
                              placement="right"
                              arrow
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setIsExtendedThinking(false);
                                  localStorage.setItem(
                                    "fp_thinking_effort",
                                    "standard"
                                  );
                                  setShowThinkingDropdown(false);
                                  if (onExtendedThinkingChange) {
                                    onExtendedThinkingChange(false);
                                  }
                                  inputRef.current?.focus();
                                }}
                                className={`flex w-full items-center gap-3 px-3 py-2 text-sm ${
                                  !isExtendedThinking
                                    ? "text-gray-900"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span className="flex-1 text-left">
                                  Standard
                                </span>
                                {!isExtendedThinking && (
                                  <CheckIcon className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </Tooltip>
                            <Tooltip
                              title="Better answers for complex questions"
                              placement="right"
                              arrow
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setIsExtendedThinking(true);
                                  localStorage.setItem(
                                    "fp_thinking_effort",
                                    "extended"
                                  );
                                  setShowThinkingDropdown(false);
                                  if (onExtendedThinkingChange) {
                                    onExtendedThinkingChange(true);
                                  }
                                  inputRef.current?.focus();
                                }}
                                className={`flex w-full items-center gap-3 px-3 py-2 text-sm ${
                                  isExtendedThinking
                                    ? "text-gray-900"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span className="flex-1 text-left">
                                  Extended
                                </span>
                                {isExtendedThinking && (
                                  <CheckIcon className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {isReport && selectionControls}
              </div>

              {/* File tree - only show when expanded */}
              {showFileTree && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-2">{searchBar}</div>
                  <div className="max-h-[400px] overflow-auto [&_table]:border-none [&_td]:border-none [&_td]:min-w-0 [&_td]:py-1 [&_td]:px-2">
                    {treeContent}
                  </div>
                </div>
              )}
            </div>
          )}

        {toolId &&
          queryInputStatus !== "failed" &&
          queryInputStatus !== "writing_query" && (
            <div className="h-[4px] overflow-hidden rounded-full mt-4">
              <FpProgressBar
                runForSec={
                  toolExecutionAvgTime[
                    toolId as keyof typeof toolExecutionAvgTime
                  ]
                }
                type={toolId}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default React.memo(QueryInput);
